
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Task } from '@/types/task';
import { useToast } from '@/hooks/use-toast';

export const useAddTaskMutation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskData: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => {
      try {
        console.log('Creating new task');

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        const { data, error } = await supabase
          .from('tasks')
          .insert([{
            title: taskData.title,
            description: taskData.description || null,
            hebrew_title: taskData.hebrewTitle || null,
            hebrew_description: taskData.hebrewDescription || null,
            scheduled_day: taskData.scheduledDay || null,
            scheduled_hour: taskData.scheduledHour !== undefined ? Number(taskData.scheduledHour) : null,
            scheduled_minute: taskData.scheduledMinute !== undefined ? Number(taskData.scheduledMinute) : null,
            coins: taskData.coins || 0,
            is_completed: false,
            user_id: user.id
          }])
          .select()
          .single();

        if (error) {
          console.error('Error inserting task:', error);
          throw error;
        }

        console.log('Task created successfully:', data);
        return data;
      } catch (error) {
        console.error('Error in mutationFn:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Task creation successful, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast({
        title: "Success",
        description: "Task added successfully!",
      });
    },
    onError: (error: Error) => {
      console.error('Task creation error:', error);
      toast({
        title: "Error",
        description: `Failed to add task: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};
